import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { QcStep } from '../../utils/qc-steps';
import { QC_STEP_LABELS, QC_STEPS } from '../../utils/qc-steps';

/** Where a tab stands: done, the page you're on, still to come, or not open yet. */
type TabState = 'done' | 'current' | 'upcoming' | 'locked';

interface QcStepTabsProps {
  current: QcStep;
  /** Which steps this run includes (from `qcStepsFor`); the New page passes its live choice. */
  steps: Record<QcStep, boolean>;
  /** The run the tabs link into; none on the New page, where only Set up is reachable. */
  runId?: string | undefined;
  /** Short status shown after the current tab's label, e.g. "Finished in 4m 12s". */
  status?: string | undefined;
  /** Steps already done (e.g. Run once the job finished); Set up is always done on a run. */
  done?: Partial<Record<QcStep, boolean>>;
  /** A short figure after a step's label — "26/40" reviewed, the score after — when cheap to know. */
  badges?: Partial<Record<QcStep, string>>;
  /**
   * Steps that exist but can't be opened yet, with the reason as their tooltip ("Opens when the
   * run ends", "Starts when you start the check") — shown dimmed with `cursor-not-allowed`.
   */
  locked?: Partial<Record<QcStep, string>>;
  /** Hover tooltips on open tabs ("Done · 3m 50s", "Scores 62.8 → 82.4"). */
  tips?: Partial<Record<QcStep, string>>;
  /** The run's own tab labels (`qcStepLabels`); defaults to `QC_STEP_LABELS`. */
  labels?: Record<QcStep, string> | undefined;
}

/** Number badge per state — a tick when done. */
function Badge({ index, state }: { index: number; state: TabState }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
        state === 'current' && 'bg-primary text-primary-foreground',
        state === 'done' && 'bg-success text-white',
        (state === 'upcoming' || state === 'locked') &&
          'border-border text-muted-foreground border',
      )}
    >
      {state === 'done' ? <Check className="size-3" strokeWidth={3} /> : index + 1}
    </span>
  );
}

/**
 * The QC flow as one slim numbered tab strip under the run header — the only stepper on a QC
 * page (the run's own progress tracker is the circles-and-track one). Only the steps the run
 * includes are shown, numbered in order; `locked` steps (not open yet) say why in a tooltip, and
 * `tips` add a hover line to open ones.
 */
export function QcStepTabs({
  current,
  steps,
  runId,
  status,
  done = {},
  badges = {},
  locked = {},
  tips = {},
  labels = QC_STEP_LABELS,
}: QcStepTabsProps) {
  return (
    <nav aria-label="QC steps" className="border-border/70 -mb-px flex overflow-x-auto border-b">
      <ol className="flex min-w-max items-center gap-6">
        {QC_STEPS.filter((step) => steps[step]).map((step, index) => {
          const state: TabState =
            step === current
              ? 'current'
              : locked[step]
                ? 'locked'
                : done[step]
                  ? 'done'
                  : 'upcoming';
          const label = (
            <>
              <Badge index={index} state={state} />
              <span>{labels[step]}</span>
              {badges[step] && state !== 'locked' && (
                <span
                  className={cn(
                    'rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                    state === 'current'
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {badges[step]}
                </span>
              )}
              {state === 'current' && status && (
                <span className="text-muted-foreground text-xs font-normal">· {status}</span>
              )}
            </>
          );
          const base =
            'flex h-11 items-center gap-2 border-b-2 px-0.5 text-sm font-medium whitespace-nowrap';
          // Set up has no page on an existing run; the run steps link via `?step=`.
          const linkable = state !== 'locked' && state !== 'current' && step !== 'setup' && runId;
          const tip = state === 'locked' ? locked[step] : tips[step];
          if (state === 'locked') {
            return (
              <li key={step}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      aria-disabled="true"
                      className={cn(
                        base,
                        'text-muted-foreground/60 cursor-not-allowed border-transparent',
                      )}
                    >
                      {label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{tip}</TooltipContent>
                </Tooltip>
              </li>
            );
          }
          const tab = linkable ? (
            <Link
              to="/content/qc/$runId"
              params={{ runId }}
              search={(prev: Record<string, unknown>) => ({
                ...prev,
                step,
              })}
              className={cn(
                base,
                'text-foreground hover:text-primary cursor-pointer border-transparent transition-colors',
                state === 'upcoming' && 'text-muted-foreground',
              )}
            >
              {label}
            </Link>
          ) : (
            <span
              aria-current={state === 'current' ? 'step' : undefined}
              className={cn(
                base,
                state === 'current' && 'border-primary text-primary',
                state === 'done' && 'text-foreground border-transparent',
                state === 'upcoming' && 'text-muted-foreground border-transparent',
              )}
            >
              {label}
            </span>
          );
          return (
            <li key={step}>
              {tip ? (
                <Tooltip>
                  <TooltipTrigger asChild>{tab}</TooltipTrigger>
                  <TooltipContent>{tip}</TooltipContent>
                </Tooltip>
              ) : (
                tab
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
